import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CancelOutlined, CheckCircleOutline } from '@mui/icons-material';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import type { AdminClientUser } from '@services/admin-users.service.ts';
import { pmUsersService } from '@services/pm-users.service.ts';
import { PlanChip } from '@components/admin/PlanChip.tsx';
import { ConnectionsByStatusChips } from '@components/admin/ConnectionsByStatusChips.tsx';
import { UserStatusChip } from '@components/admin/UserStatusChip.tsx';
import { toastService } from '@services/toast.service.ts';

// Клієнти для продакт-менеджера (2026-10-10). Окремий клон адмінської сторінки /admin/users/clients лише з основою:
// список, пошук, статус, перехід у картку клієнта (/pm/client/:id). Дії адміна (деталі, блокування, тариф, ролі, цілі) свідомо не перенесені - сторінку
// доробляємо під продакта окремо.
const PmClientsPageContent: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [users, setUsers] = useState<AdminClientUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await pmUsersService.getClients({
        page,
        limit,
        search: searchQuery || undefined,
        status: statusFilter ? Number(statusFilter) : undefined,
      });
      setUsers(result.data);
      setTotal(result.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити список клієнтів.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <Box data-i-doc={PageIndices.PM_CLIENTS}>
      <PageHint iDoc={PageIndices.PM_CLIENTS} />

      <PageCard>
        <PageToolbar
          storageKey="filters:pm-clients"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за іменем, email або ID',
              value: searchQuery,
              onChange: (v) => { setSearchQuery(v); setPage(1); },
              flexGrow: 1,
            },
            {
              id: 'status',
              type: 'select',
              label: 'Статус',
              value: statusFilter,
              onChange: (v) => { setStatusFilter(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                { value: '1', label: 'Активний' },
                { value: '2', label: 'Заблокований' },
                { value: '3', label: 'Видалений' },
              ],
            },
          ]}
          onReset={() => { setSearchQuery(''); setStatusFilter(''); setPage(1); }}
          showReset
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>Клієнтів не знайдено.</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Аватар</TableCell>
                  <TableCell>Ім'я / Email / Телефон</TableCell>
                  <TableCell>Часовий пояс</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Тариф</TableCell>
                  <TableCell>Експертів</TableCell>
                  <TableCell>Реєстрація</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow
                    hover
                    key={user.id}
                    onClick={() => navigate(`/pm/client/${user.id}/dashboard`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{user.id}</Typography>
                    </TableCell>
                    <TableCell>
                      <UserAvatar path={user.avatar} name={user.name} sx={{ width: 32, height: 32 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                        {user.isEmailVerified ? (
                          <Tooltip title="Email верифіковано">
                            <CheckCircleOutline color="success" sx={{ fontSize: 13 }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Email не верифіковано">
                            <CancelOutlined color="disabled" sx={{ fontSize: 13 }} />
                          </Tooltip>
                        )}
                      </Box>
                      {user.phone && (
                        <Typography variant="caption" color="text.secondary">{user.phone}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>{user.timezone ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <UserStatusChip status={user.status} isProtectedDemoAccount={user.isProtectedDemoAccount} />
                    </TableCell>
                    <TableCell>
                      <PlanChip subscription={user.subscription} billingExempt={user.billingExempt} />
                    </TableCell>
                    <TableCell><ConnectionsByStatusChips connectionsByStatus={user.connectionsByStatus} /></TableCell>
                    <TableCell>{new Date(user.createdAt).toLocaleDateString('uk-UA')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:pm-clients"
        />
      </PageCard>
    </Box>
  );
};

export const PmClientsPage: React.FC = () => (
  <DashboardLayout>
    <PmClientsPageContent />
  </DashboardLayout>
);
